/**
 * <StatutoryDefaultsCallout /> — KSA Labor Law baseline callout
 * (M21.14, criterion 22).
 *
 * Embedded near the top of each Policies page (Leave Types, Loan
 * Products, Allowances, Grades). Fetches the curated statutory
 * reference for the domain and shows each item's statutory value +
 * Arabic-source Article citation, so HR always sees the legal floor
 * next to whatever they've customised on the page below.
 *
 * Collapsible — defaults open so the baseline is visible, but HR
 * can fold it away once they've internalised it.
 */

import { useQuery } from "@tanstack/react-query";
import { ChevronDown, ChevronRight, Scale } from "lucide-react";
import { useState } from "react";

import { api } from "../../lib/api";

type Domain = "leave" | "loan" | "allowance" | "grade";

export interface StatutoryDefaultsCalloutProps {
  domain: Domain;
}

export function StatutoryDefaultsCallout({
  domain,
}: StatutoryDefaultsCalloutProps) {
  const [open, setOpen] = useState(true);
  const q = useQuery({
    queryKey: ["statutory-defaults", domain],
    queryFn: () => api.getStatutoryDefaults(domain),
    staleTime: 60 * 60_000, // reference data — rarely changes
  });

  if (q.isLoading || q.error || !q.data) return null;

  return (
    <section className="rounded-xl border border-amber-200 bg-amber-50/60">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="flex w-full items-center gap-2 px-4 py-3 text-start"
      >
        <Scale size={15} className="text-amber-700" />
        <span className="text-sm font-semibold text-amber-900">
          KSA Labor Law defaults
        </span>
        <span className="text-xs text-amber-700">
          — the statutory floor your customizations sit on top of
        </span>
        {open ? (
          <ChevronDown size={15} className="ms-auto text-amber-700" />
        ) : (
          <ChevronRight size={15} className="ms-auto text-amber-700" />
        )}
      </button>
      {open && (
        <ul className="divide-y divide-amber-200 border-t border-amber-200">
          {q.data.items.map((item) => (
            <li
              key={item.key}
              className="flex flex-col gap-0.5 px-4 py-2.5 sm:flex-row sm:items-baseline sm:gap-3"
            >
              <span className="text-sm font-medium text-amber-900 sm:w-64 sm:shrink-0">
                {item.label}
              </span>
              <span className="text-sm font-semibold text-amber-950">
                {item.statutory_value}
              </span>
              <span className="text-xs text-amber-700 sm:ms-auto">
                {item.citation}
              </span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
